import React from 'react';
import { Box, Grid, Typography } from '@mui/material';

import Button from 'modules/views/shared/Button';

import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import * as Yup from 'yup';

import TextField from 'modules/views/shared/TextField';
import PasswordField from 'modules/views/shared/PasswordField';
import useForm from 'hooks/useForm';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { changePassword } from 'modules/state-mgmt/user';
import { prettifyErrorResponse } from 'utils';
import { actionMapForOtherLoaders } from 'constants/actions';
import { getPasswordRules, validatePassword } from 'utils/passwordRules';
import { useStyles } from './styles';

interface ChangePasswordProps {
  isAdminSessionContext?: boolean;
}

export const ChangePassword: React.FC<ChangePasswordProps> = ({ isAdminSessionContext }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { email, username, changePasswordLoading, isWorker, isAdmin } = useAppSelector(state => ({
    email: isAdminSessionContext
      ? (state.workerData.adminContacts?.emails?.find((c: any) => c.isPrimary)?.contactValue ?? '')
      : state.user.email,
    username: state.user.username,
    changePasswordLoading: state.loader.otherLoaders?.[actionMapForOtherLoaders.changePassword],
    isWorker: state.user?.isWorker,
    isAdmin: state.user?.isAdmin,
  }));

  const changePasswordSchema = Yup.object({
    oldPassword: Yup.string().required(t('myProfile.changePassword.errors.oldPasswordRequired')),
    newPassword: Yup.string()
      .required(t('myProfile.changePassword.errors.newPasswordRequired'))
      .test('password-validations', t('myProfile.changePassword.errors.invalidPassword'), function (value) {
        return validatePassword(value, { email, mobilePhoneNumber: username }, getPasswordRules(t));
      }),
    confirmPassword: Yup.string()
      .required(t('myProfile.changePassword.errors.confirmPasswordRequired'))
      .oneOf([Yup.ref('newPassword'), null], t('myProfile.changePassword.errors.confirmPasswordMismatch')),
  });

  const handleChangePassword = (values: { oldPassword: string; newPassword: string; confirmPassword: string }) => {
    // isWorker mean cognito user is worker that is changing own password,
    const isWorkerChangingPasswordInAdminSession = !isAdminSessionContext && !isWorker;
    dispatch(
      changePassword({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        isWorkerChangingPasswordInAdminSession,
      }),
    ).then(res => {
      if (res.type.includes('fulfilled')) {
        enqueueSnackbar(t('myProfile.changePassword.successMessages.passwordChanged'), { variant: 'success' });
        resetForm();
      } else {
        enqueueSnackbar(prettifyErrorResponse((res as any).payload), { variant: 'error' });
      }
    });
  };

  const { values, errors, handleSubmit, setValue, resetForm } = useForm({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: changePasswordSchema,
    onSubmit: handleChangePassword,
  });

  const isLoading = changePasswordLoading?.isLoading;
  const submitOnEnter = useSubmitOnEnter(handleSubmit, isLoading);

  return (
    <Box className={classes.changePasswordContainer} data-testid="change-password-form">
      <Typography className={classes.title}>{t('myProfile.changePassword.title')}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label={t('myProfile.changePassword.oldPassword')}
            name="oldPassword"
            testId="oldPassword-input"
            required={true}
            value={values.oldPassword}
            error={errors.oldPassword?.message}
            type={isAdmin ? 'text' : 'password'}
            maskText={isAdmin}
            onChange={e => setValue('oldPassword', e.target.value)}
            autoComplete={isAdmin ? 'off' : 'current-password'}
          />
        </Grid>
        <Grid item xs={12}>
          <PasswordField
            label={t('myProfile.changePassword.newPassword')}
            name="newPassword"
            testId="newPassword-input"
            required={true}
            value={values.newPassword}
            error={errors.newPassword?.message}
            email={email}
            mobilePhoneNumber={username}
            passwordMatchContainerClass={classes.passwordField}
            onChange={e => setValue('newPassword', e.target.value)}
            maskText={isAdmin}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('myProfile.changePassword.confirmPassword')}
            name="confirmPassword"
            testId="confirmPassword-input"
            required={true}
            value={values.confirmPassword}
            error={errors.confirmPassword?.message}
            type={isAdmin ? 'text' : 'password'}
            maskText={isAdmin}
            onChange={e => setValue('confirmPassword', e.target.value)}
            onKeyDown={submitOnEnter}
            autoComplete={isAdmin ? 'off' : 'new-password'}
          />
        </Grid>
      </Grid>
      <div className={classes.buttonContainer}>
        <Button
          label={t('myProfile.changePassword.buttons.saveChanges')}
          isLoading={isLoading}
          styleClass={classes.saveButton}
          testId="change-password-save-button"
          onClick={handleSubmit}
        />
      </div>
    </Box>
  );
};
